import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { CabButton, CabModal, CabTabs, CabTextInput } from '@CabComponents';
import DuplicateMeetingModal, {
  DuplicateMeetingSubmitProps
} from '../../../components/Schedule/DuplicateMeetingModal/DuplicateMeetingModal';
import NewMeetingModal, { NewMeetingSubmitProps } from '../NewMeetingModal/NewMeetingModal';
import ScheduleShareModalContainer from "../../../components/Schedule/ScheduleShareModal";
import MobileBlockedModal from '../../../components/Common/MobileBlockedModal/MobileBlockedModal';
import { useConfirmationModalWithUI } from '../../../components/Common/ConfirmationModal';
import {
  FetchMeetingsResponse, FetchReturn, Leader, Meeting, MeetingSlot, MeetingStatus, UpdateMeetingResponse
} from '../../../store';
import { isMobile } from '../../../utils/screenSizeUtils';
import { sortMeetings } from '../../../utils/scheduleUtils';
import { CabTimePicker } from '@CabComponents/CabTimePicker';
import UserPrefCabDatePicker from '@CabComponents/CabDatePicker/UserPrefDatePicker';
import MeetingLogTable, { MeetingInfo, FilterSortState } from './MeetingLogTable';
import { GridColumnVisibilityModel, GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';

type GridSortItem = GridSortModel[number];


export interface MeetingLogProps {
  meetingsLoaded: boolean;
  meetingSlots: MeetingSlot[];
  meetings: MeetingInfo[];
  leaders: Leader[];
  polls?: boolean;
  reusable?: boolean;
  onFetchMeetings: (
    unscheduled: boolean, completed: boolean, checkMeetingIds: number[],
    templateParentId?: number, page?: number, filterSortState?: FilterSortState
  ) => Promise<FetchReturn<FetchMeetingsResponse> | undefined>;
  onStatusChange: (meeting: Meeting, status: number) => Promise<void>;
  onPriorityChange: (meeting: Meeting, priority: number) => Promise<void>;
  onNotesChange: (meeting: Meeting, notes: string) => void;
  onNewMeeting: () => void;
  onEditMeeting: (meetingId: number) => void;
  onSeeReuslts: (meetingId: number) => void;
  onDuplicateMeeting: (meetingId: number) => void;
  onDeleteMeeting: (meeting: Meeting) => void;
  onShareMeeting: (meetingId: number) => void;
  onCopyLink: (meetingId: number) => void;
  onFetchMeetingSlots: (unscheduled: boolean) => Promise<void>;
  onShowQuestionsModalClick: (questionsString: string) => void;
  openSetScheduledDate: (mtg: Meeting) => void;
  tableFilter: GridFilterModel;
  tableSort: GridSortModel;
  columnVisibility: GridColumnVisibilityModel;
  columnOrder?: string[];
  onSetTableFilters: (filters: GridFilterModel | undefined) => void;
  onSetColumnSort: (sort: GridSortItem[]) => void;
  onSetColumnVisibility: (model: GridColumnVisibilityModel) => void;
  onColumnOrderChange?: (newOrder: string[]) => void;
  onSetIsTemplateParent: (isTemplateParent: boolean) => void;
}

const MeetingLog = ({
  meetingsLoaded, meetings, leaders, polls = false, reusable = false, onFetchMeetings, onStatusChange, onNewMeeting,
  onEditMeeting, onDeleteMeeting, onDuplicateMeeting, onShareMeeting, onCopyLink, onSeeReuslts, onFetchMeetingSlots, 
  onPriorityChange, onNotesChange, onShowQuestionsModalClick, openSetScheduledDate, tableFilter, tableSort,
  columnVisibility, columnOrder, onSetTableFilters, onSetColumnSort, onSetColumnVisibility,
  onColumnOrderChange, onSetIsTemplateParent, meetingSlots
}: MeetingLogProps): ReactElement => {
  const [fetchingMeetings, setFetchingMeetings] = useState(false);
  const [completedMeetingCount, setCompletedMeetingCount] = useState(0);
  const [pendingMessage, setPendingMessage] = useState('');
  const [completedMessage, setCompletedMessage] = useState('not set');
  const [isExporting, setIsExporting] = useState(false);


  // filter by status but not for reusable rows with a template_parent
  const inProgressMeetings = useMemo(() => (
    meetings
      .filter(mtg => mtg.status === MeetingStatus.PENDING || (reusable && mtg.template_parent))
      .slice().sort(sortMeetings)
  ), [meetings, reusable]);

  const completedMeetings = useMemo(() => (
    meetings
      .filter(mtg => mtg.status !== MeetingStatus.PENDING || (reusable && mtg.template_parent))
      .slice().sort(sortMeetings)
  ), [meetings, reusable]);

  const checkMeetingIds = completedMeetings.filter(mtg => !mtg.template_parent).map(mtg => mtg.id);

  useEffect(() => {
    if (polls) {
      setPendingMessage('pending polls');
      setCompletedMessage('scheduled polls');
    } else if (reusable) {
      setPendingMessage('pending reusables');
      setCompletedMessage('scheduled reusables');
    } else {
      setPendingMessage('pending meetings');
      setCompletedMessage('scheduled meetings');
    }
  }, [polls, reusable]);

  const handleFetchInProgressMeetings = useCallback(async (
    templateParentId?: number, page?: number, filterSortState?: FilterSortState
  ) => {
    setFetchingMeetings(true);
    const res = await onFetchMeetings(true, false, [], templateParentId, page, filterSortState);
    setFetchingMeetings(false);
    return res;
  }, [onFetchMeetings]);

  const handleFetchCompletedMeetings = useCallback(async (
    templateParentId?: number, page?: number, filterSortState?: FilterSortState
  ) => {
    setFetchingMeetings(true);
    const res = await onFetchMeetings(false, true, checkMeetingIds, templateParentId, page, filterSortState);
    if (res) {
      const count = 'data' in res && 'meta' in res.data && res.data.meta ? res.data.meta.count : 0;
      setCompletedMeetingCount(count);
    }
    setFetchingMeetings(false);
    return res;
    // needs deep comparison
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(checkMeetingIds), onFetchMeetings]);

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        marginBottom: 2
      }}>
      <CabTabs
        tabs={[{ label: reusable ? 'Active' : 'Pending' }, { label: reusable ? 'Inactive' : 'Completed' }]}
        align="left"
        hideDivider
        disabled={isExporting}
        sx={{ marginTop: 2, flex: 1, marginBottom: 4 }}
        tabPanelSx={{ marginTop: 0, height: '100%', position: 'relative' }}
      >
        <MeetingLogTable
          meetings={inProgressMeetings}
          leaders={leaders}
          loading={!meetingsLoaded || fetchingMeetings}
          polls={polls}
          reusable={reusable}
          onStatusChange={onStatusChange}
          onPriorityChange={onPriorityChange}
          onMeetingClick={polls ? onSeeReuslts : onEditMeeting}
          onEditMeeting={onEditMeeting}
          onSeeResults={onSeeReuslts}
          onDeleteMeeting={onDeleteMeeting}
          onDuplicateMeeting={onDuplicateMeeting}
          onNewMeeting={onNewMeeting}
          onShareMeeting={onShareMeeting}
          onCopyLink={onCopyLink}
          sortableStatus={false}
          onFetchMeetings={handleFetchInProgressMeetings}
          onFetchMeetingSlots={() => onFetchMeetingSlots(true)}
          onNotesChange={onNotesChange}
          onShowQuestionsModalClick={onShowQuestionsModalClick}
          emptyMessage={pendingMessage}
          openSetScheduledDate={openSetScheduledDate}
          fetchDataOnPaginateOrFilter={false}
          tableSort={tableSort}
          tableFilter={tableFilter}
          columnVisibility={columnVisibility}
          columnOrder={columnOrder}
          onSetTableFilters={onSetTableFilters}
          onSetColumnSort={onSetColumnSort}
          onSetColumnVisibility={onSetColumnVisibility}
          onColumnOrderChange={onColumnOrderChange}
          onSetIsTemplateParent={onSetIsTemplateParent}
          meetingSlots={meetingSlots}
          isExporting={isExporting}
          onSetIsExporting={setIsExporting}
          showTimesOfferedColumn={true}
          showSelectedScheduledColumn={reusable}
          showDateScheduledColumn={true}
        />
        <MeetingLogTable
          meetings={completedMeetings}
          rowCount={completedMeetingCount}
          leaders={leaders}
          loading={fetchingMeetings}
          polls={polls}
          reusable={reusable}
          onStatusChange={onStatusChange}
          onPriorityChange={onPriorityChange}
          onMeetingClick={polls ? onSeeReuslts : onEditMeeting}
          onEditMeeting={onEditMeeting}
          onSeeResults={onSeeReuslts}
          onDeleteMeeting={onDeleteMeeting}
          onDuplicateMeeting={onDuplicateMeeting}
          onNewMeeting={onNewMeeting}
          onShareMeeting={onShareMeeting}
          onCopyLink={onCopyLink}
          paginationMode="server"
          filterMode="server"
          sortMode="server"
          onFetchMeetings={handleFetchCompletedMeetings}
          onFetchMeetingSlots={() => onFetchMeetingSlots(false)}
          onNotesChange={onNotesChange}
          onShowQuestionsModalClick={onShowQuestionsModalClick}
          emptyMessage={completedMessage}
          openSetScheduledDate={openSetScheduledDate}
          fetchDataOnPaginateOrFilter={true}
          tableSort={tableSort}
          tableFilter={tableFilter}
          columnVisibility={columnVisibility}
          columnOrder={columnOrder}
          onSetTableFilters={onSetTableFilters}
          onSetColumnSort={onSetColumnSort}
          onSetColumnVisibility={onSetColumnVisibility}
          onColumnOrderChange={onColumnOrderChange}
          onSetIsTemplateParent={onSetIsTemplateParent}
          meetingSlots={meetingSlots}
          isExporting={isExporting}
          onSetIsExporting={setIsExporting}
          showTimesOfferedColumn={false}
          showSelectedScheduledColumn={true}
          showDateScheduledColumn={true}
        />
      </CabTabs>
    </Box>
  );
};


export type MeetingLogPageProps = Omit<
  MeetingLogProps, 'onNewMeeting'|'onDuplicateMeeting'|'onShareMeeting'|'onDeleteMeeting'|
  'openSetScheduledDate' | 'onNotesChange' | 'onShowQuestionsModalClick' | 'onSetIsTemplateParent'>
& {
  onNewMeeting: (data: NewMeetingSubmitProps) => Promise<void>;
  onDuplicateMeeting: (meetingId: number, data: DuplicateMeetingSubmitProps) => Promise<Meeting|undefined>;
  onDeleteMeeting: (meeting: Meeting) => Promise<void>;
  onNotesChange: (meeting: Meeting, notes: string) => Promise<UpdateMeetingResponse | undefined>
  onScheduleDateChange: (mtg: Meeting, selected_scheduled_time: string | null) => void
};

const MeetingLogPage = ({ 
  onNewMeeting, onEditMeeting, onSeeReuslts, onDeleteMeeting, onNotesChange, onDuplicateMeeting, onCopyLink,
  onScheduleDateChange, ...props 
}: MeetingLogPageProps) => {
  const [showMobileBlockedModal, setShowMobileBlockedModal] = useState(false);
  const [showNewMeeting, setShowNewMeeting] = useState(false);
  const [duplicateMeetingId, setDuplicateMeetingId] = useState<number|null>(null);
  const [notesMeeting, setNotesMeeting] = useState<Meeting|null>(null);

  const { confirm: confirmDelete, ConfirmationModalComponent: DeleteConfirmationModal } = useConfirmationModalWithUI();
  const [scheduleShareMeetingId, setScheduleShareMeetingId] = useState<number|null>(null);
  const [notes, setNotes] = useState(notesMeeting?.notes || '');
  const [showQuestions, setShowQuestions] = useState<string|null>(null);
  const [isTemplateParent, setIsTemplateParent] = useState(false);
  const [showScheduledDate, setShowScheduledDate] = useState<Meeting | null>(null);
  const [scheduleDate, setScheduleDate] = useState<DateTime | null>(null);

  const handleNewMeeting = useCallback(async (data: NewMeetingSubmitProps) => {
    await onNewMeeting(data);
    setShowNewMeeting(false);
  }, [onNewMeeting]);

  const handleEditMeeting = useCallback((meetingId: number) => {
    if (!isMobile()) {
      onEditMeeting(meetingId);
    } else {
      setShowMobileBlockedModal(true);
    }
  }, [onEditMeeting]);

  const handleSeeResults = useCallback((meetingId: number) => {
    onSeeReuslts(meetingId);
  }, [onSeeReuslts]);

  const handleDuplicateMeeting = useCallback(async (data: DuplicateMeetingSubmitProps) => {
    if (duplicateMeetingId == null) return;

    const meeting = await onDuplicateMeeting(duplicateMeetingId, data);
    if (meeting) {
      setDuplicateMeetingId(null);
      setScheduleShareMeetingId(meeting.id);
      return meeting;
    }
  }, [duplicateMeetingId, onDuplicateMeeting]);

  const handleDeleteMeeting = useCallback(async (meeting: Meeting) => {
    const confirmed = await confirmDelete({
      title: "Confirm Deletion",
      message: meeting.is_poll 
        ? 'Are you sure you want to delete this poll?' 
        : 'Are you sure you want to delete this meeting record?',
      confirmText: "Delete", cancelText: "Cancel", isDestructive: true,
    });
    
    if (confirmed) {
      await onDeleteMeeting(meeting);
    }
  }, [confirmDelete, onDeleteMeeting]);

  const handleNotesChange = useCallback(async (meeting: Meeting) => {
    if (!meeting) return;

    await onNotesChange(meeting, notes);
    setNotesMeeting(null);
    setNotes('');
  }, [notes, onNotesChange]);

  const closeNotesModal = () => {
    setNotesMeeting(null);
    setNotes('');
  };

  const handleShowQuestionsModalClick = (questionsString: string) => {
    setShowQuestions(questionsString);
  };

  const handleShowNewMeeting = useCallback(() => setShowNewMeeting(true), []);
  const handleSetDuplicateMeeting = useCallback((id: number | null) => setDuplicateMeetingId(id), []);
  const handleSetShareMeeting = useCallback((id: number | null) => setScheduleShareMeetingId(id), []);
  const handleSetNotesMeeting = useCallback((mtg: Meeting | null, mtgNotes: string) => {
    setNotesMeeting(mtg); 
    setNotes(mtgNotes);
  }, []);

  const submitUpdateTime = (newScheduleDate: DateTime | null) => {
    if (showScheduledDate) {
      onScheduleDateChange(showScheduledDate, newScheduleDate ? newScheduleDate.toISO() : null);
      setShowScheduledDate(null);
    }
  };

  return (
    <>
      <MeetingLog
        {...props}
        onNewMeeting={handleShowNewMeeting}
        onEditMeeting={handleEditMeeting}
        onSeeReuslts={handleSeeResults}
        onDeleteMeeting={handleDeleteMeeting}
        onDuplicateMeeting={handleSetDuplicateMeeting}
        onShareMeeting={handleSetShareMeeting}
        onCopyLink={onCopyLink}
        onNotesChange={handleSetNotesMeeting}
        onShowQuestionsModalClick={handleShowQuestionsModalClick}
        openSetScheduledDate={(mtg: Meeting) => {
          setShowScheduledDate(mtg);
          setScheduleDate(mtg.selected_scheduled_time ? DateTime.fromISO(mtg.selected_scheduled_time) : null);
        }}
        onSetIsTemplateParent={setIsTemplateParent}
      />
      <MobileBlockedModal
        open={showMobileBlockedModal}
        onClose={() => setShowMobileBlockedModal(false)}
        message='You can visit Cabinet in your desktop browser at app.joincabinet.com to add or update meetings.'
      />
      <NewMeetingModal
        open={showNewMeeting}
        onClose={() => setShowNewMeeting(false)}
        submitNew={handleNewMeeting}
        leaders={props.leaders}
        isPoll={props.polls}
        isReusable={props.reusable}
      />
      <DuplicateMeetingModal
        open={duplicateMeetingId != null}
        meeting={duplicateMeetingId ? props.meetings.find(m => m.id === duplicateMeetingId) : undefined}
        onClose={() => setDuplicateMeetingId(null)}
        submitDuplicate={handleDuplicateMeeting}
      />
      <DeleteConfirmationModal />
      {scheduleShareMeetingId &&
        <ScheduleShareModalContainer
          modalOpen={scheduleShareMeetingId != null}
          meetingId={scheduleShareMeetingId}
          onModalClose={() => setScheduleShareMeetingId(null)}
          showEdit
        />
      }
      {notesMeeting &&
      <CabModal
        open={notesMeeting != null}
        onClose={closeNotesModal}
        title={'Meeting Notes'}
        closeIcon
        actionButtons={
          <Box>
            <CabButton buttonType="tertiary" sx={{ marginRight: 1 }} onClick={closeNotesModal}>
              Cancel
            </CabButton>
            <CabButton buttonType="primary" onClick={() => handleNotesChange(notesMeeting)}>
              Save
            </CabButton>
          </Box>
        }
      >
        <CabTextInput
          fullWidth
          maxRows={4}
          minRows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value || '')}
          multiline
        />
      </CabModal>
      }
      {showQuestions &&
      <CabModal
        open={showQuestions != null}
        onClose={() => setShowQuestions(null)}
        title={ isTemplateParent ? 'Questions' : 'Questions and Answers'}
        closeIcon
      >
        <CabTextInput
          fullWidth
          maxRows={10}
          minRows={2}
          value={showQuestions}
          multiline
        />
      </CabModal>
      }
      <CabModal
        open={showScheduledDate !== null}
        title="Set Meeting Start Time"
        onClose={() => setShowScheduledDate(null)}
        closeIcon
        actionButtons={<>
          <CabButton 
            buttonType="tertiary"
            color="error"
            onClick={() => {
              setScheduleDate(null);
              setShowScheduledDate(null);
              submitUpdateTime(null);
            }}
          >Remove Time</CabButton>
          <CabButton onClick={() => submitUpdateTime(scheduleDate)}>Save Time</CabButton>
        </>}
      >
        <Typography>When is this meeting scheduled to begin?</Typography>
        {(showScheduledDate) &&
          <Box
            sx={{
              display: "flex",
              flexWrap: "nowrap"
            }}>
            <UserPrefCabDatePicker
              value={scheduleDate?.toISO() || null}
              onChange={(selectedScheduledTime) => setScheduleDate(
                selectedScheduledTime ? DateTime.fromISO(selectedScheduledTime) : null
              ) }
              
            />
            <Box 
              sx={{
                marginLeft: 1
              }}
            >
              <CabTimePicker
                pickerTime={scheduleDate ? `${scheduleDate.hour}:${scheduleDate.minute}` : ""}
                onChange={(selectedScheduledTime) => {
                  const [hours, minutes] = selectedScheduledTime.split(":");
                  if (scheduleDate) {
                    setScheduleDate(
                      scheduleDate.set({hour: Number(hours), minute: Number(minutes)})
                    );
                  } else {
                    const tempTime = DateTime.now();
                    setScheduleDate(
                      tempTime.set({hour: Number(hours), minute: Number(minutes)})
                    );
                  }
                }}
              />
            </Box>
          </Box>
        }
      </CabModal>
    </>
  );
};

export default MeetingLogPage;
